<template>
  <div class="mt-4 text-left" data-testid="agenda-type-field">
    <span
      class="inline-block rounded bg-primary px-2 py-0.5 text-[10px] font-bold tracking-wide text-white"
    >
      NOVO
    </span>
    <p
      class="mt-2"
      :class="mode === 'create' ? 'font-bold text-base text-[#323232]' : 'text-gray-500 text-base'"
    >
      Tipo de agenda
      <span class="text-xs font-normal text-gray-500">
        · editar exige <code>schedule_editing</code>
      </span>
    </p>
    <p class="mb-3 mt-1 text-sm text-gray-600">
      Define como a tela <b>Demanda</b> mede esta escala. Não altera plantões, pagamentos nem
      políticas.
    </p>

    <div :class="{ 'opacity-60': readonly }">
      <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div
          v-for="opt in options"
          :key="opt.value"
          :data-testid="`agenda-type-card-${opt.value}`"
          :class="{ 'pointer-events-none': readonly }"
        >
          <SelectableCard
            :title="opt.title"
            :description="opt.description"
            :selected="modelValue === opt.value"
            @click="select(opt.value)"
          >
            <p
              v-if="opt.value === 'AUTO'"
              class="mt-2 rounded px-2 py-1 text-xs"
              :class="detectionClass"
              data-testid="agenda-type-detection"
            >
              <template v-for="(seg, i) in detectionView.segments" :key="i">
                <b v-if="seg.bold">{{ seg.text }}</b>
                <template v-else>{{ seg.text }}</template>
              </template>
            </p>
          </SelectableCard>
        </div>
      </div>
      <p v-if="readonly" class="mt-2 text-sm text-gray-600" data-testid="agenda-type-lock">
        🔒 Somente leitura — alterar o tipo de agenda requer a permissão
        <code>schedule_editing</code> nesta escala.
      </p>
    </div>

    <div
      class="mt-3 flex gap-2 rounded-lg border p-3 text-sm text-gray-700"
      :class="
        impact.type === 'VARIABLE' ? 'border-amber-200 bg-amber-50' : 'border-sky-200 bg-sky-50'
      "
      data-testid="agenda-type-impact"
    >
      <span aria-hidden="true">{{ impact.type === 'VARIABLE' ? '∿' : '≋' }}</span>
      <div v-if="impact.type === 'CONTINUOUS'">
        Na tela <b>Demanda</b>, as horas desta escala entram em <b>Cobertura contínua</b>:
        <ul class="ml-5 list-disc">
          <li>
            alerta de <b>queda vs padrão</b> (≥ 5% abaixo da mediana de 12 semanas)<span
              v-if="impact.overrideWarning"
              class="text-[#9a5200]"
            >
              {{ impact.overrideWarning }}</span
            ><template v-if="impact.moderateRange">
              — como a variação é moderada, mostra a <b>faixa mín–máx</b></template
            >;
          </li>
          <li>permite <b>quadro-meta</b> e, com ele, <b>déficit</b> e “Criar vagas”.</li>
        </ul>
      </div>
      <div v-else>
        Na tela <b>Demanda</b>, as horas desta escala vão para a seção <b>Agenda variável</b>:
        <ul class="ml-5 list-disc">
          <li>vagas abertas, taxa de plantões apagados e faixa semanal (mín–máx);</li>
          <li><b>sem</b> alerta de queda, <b>sem</b> quadro-meta e <b>sem</b> déficit.</li>
        </ul>
        <p v-if="impact.overrideWarning" class="mt-1.5 text-[#9a5200]">
          {{ impact.overrideWarning }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import type { AgendaDetectionResponse } from '@/Network/Types/Responses/DemandResponses'
import {
  AGENDA_TYPE_LABELS,
  detectionText,
  impactModel,
  type AgendaFieldMode,
  type AgendaTypeValue
} from './agendaTypeModel'

const props = defineProps<{
  modelValue: AgendaTypeValue
  mode: AgendaFieldMode
  readonly: boolean
  detection: AgendaDetectionResponse | null
}>()

const emit = defineEmits<{ 'update:modelValue': [value: AgendaTypeValue] }>()

const options: { value: AgendaTypeValue; title: string; description: string }[] = [
  {
    value: 'AUTO',
    title: AGENDA_TYPE_LABELS.AUTO,
    description:
      'O sistema classifica pela variação semanal das horas criadas nas últimas 12 semanas e reclassifica sozinho.'
  },
  {
    value: 'CONTINUOUS',
    title: AGENDA_TYPE_LABELS.CONTINUOUS,
    description:
      'Setor com médico em todas as horas (UTI, PS, UPA, Maternidade). A Demanda compara com o padrão recente e aceita quadro-meta.'
  },
  {
    value: 'VARIABLE',
    title: AGENDA_TYPE_LABELS.VARIABLE,
    description:
      'Agenda que muda semana a semana (ambulatório, eletivas, consultórios). Sem déficit: mostra vagas abertas, plantões apagados e faixa semanal.'
  }
]

const detectionView = computed(() => detectionText(props.detection, props.mode))
const impact = computed(() => impactModel(props.modelValue, props.detection, props.mode))

const detectionClass = computed(() => {
  switch (detectionView.value.variant) {
    case 'wait':
      return 'bg-gray-100 text-gray-600'
    case 'variable':
      return 'bg-amber-50 text-amber-800'
    default:
      return 'bg-sky-50 text-sky-800'
  }
})

function select(value: AgendaTypeValue) {
  // Sem `schedule_editing` o campo é somente leitura: o clique não altera nada.
  if (props.readonly) return
  emit('update:modelValue', value)
}
</script>
